import { forwardRef, useState, useRef, useEffect, useCallback } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MoreVertical, Pencil, Palette, Plus, Trash2 } from 'lucide-react'
import type { Folder } from '../../lib/api'
import { FOLDER_COLORS } from './folderColors'
import { FolderContextMenu } from './FolderContextMenu'
import { FolderColorPicker } from './FolderColorPicker'

interface FolderCardProps {
  folder: Folder
  isActive?: boolean
  onClick: () => void
  onRename: (name: string) => void
  onDelete: () => void
  onChangeColor: (color: string) => void
  onAddApps: () => void
  folderAppNames: string[]
  availableApps: { name: string; display_name?: string | null }[]
  onAddAppToFolder: (appName: string) => void
  className?: string
}

export const FolderCard = forwardRef<HTMLDivElement, FolderCardProps>(
  function FolderCard(
    {
      folder,
      isActive = false,
      onClick,
      onRename,
      onDelete,
      onChangeColor,
      folderAppNames,
      availableApps,
      onAddAppToFolder,
      className = '',
    },
    ref
  ) {
    const [editing, setEditing] = useState(false)
    const [editValue, setEditValue] = useState(folder.name)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus()
        inputRef.current.select()
      }
    }, [editing])

    const commitRename = useCallback(() => {
      const trimmed = editValue.trim()
      if (trimmed && trimmed !== folder.name) {
        onRename(trimmed)
      }
      setEditing(false)
    }, [editValue, folder.name, onRename])

    const cancelRename = useCallback(() => {
      setEditValue(folder.name)
      setEditing(false)
    }, [folder.name])

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        commitRename()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        cancelRename()
      }
    }

    const colorSet = FOLDER_COLORS[folder.color] || FOLDER_COLORS.gray

    const filteredApps = availableApps.filter((app) => !folderAppNames.includes(app.name))

    const menuContent = (
      <>
        <DropdownMenu.Item
          className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
          onSelect={() => setEditing(true)}
        >
          <Pencil className="w-4 h-4" />
          Rename
        </DropdownMenu.Item>

        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none">
            <Palette className="w-4 h-4" />
            Change Color
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="bg-card border border-border rounded-lg shadow-lg p-1 z-50">
              <FolderColorPicker
                currentColor={folder.color}
                onSelect={onChangeColor}
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>

        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none">
            <Plus className="w-4 h-4" />
            Add Apps
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] max-h-[200px] overflow-y-auto z-50">
              {filteredApps.length === 0 ? (
                <DropdownMenu.Item
                  className="px-3 py-1.5 text-sm text-muted-foreground cursor-default outline-none"
                  disabled
                >
                  No apps
                </DropdownMenu.Item>
              ) : (
                filteredApps.map((app) => (
                  <DropdownMenu.Item
                    key={app.name}
                    className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer outline-none"
                    onSelect={() => onAddAppToFolder(app.name)}
                  >
                    {app.display_name || app.name}
                  </DropdownMenu.Item>
                ))
              )}
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>

        <DropdownMenu.Separator className="h-px bg-border my-1" />

        <DropdownMenu.Item
          className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none text-destructive"
          onSelect={onDelete}
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </DropdownMenu.Item>
      </>
    )

    return (
      <FolderContextMenu
        onRename={() => setEditing(true)}
        onChangeColor={onChangeColor}
        onAddApp={onAddAppToFolder}
        onDelete={onDelete}
        currentColor={folder.color}
        availableApps={availableApps}
        folderAppNames={folderAppNames}
      >
        <div
          ref={ref}
          className={`group relative flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer
            ${colorSet.bg} transition-colors min-w-[140px] sm:min-w-[160px] select-none
            ${isActive ? `ring-2 ${colorSet.ring || 'ring-primary'} border-transparent` : 'border-border hover:border-foreground/20'}
            ${className}`}
          onClick={editing ? undefined : onClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (!editing && e.key === 'Enter') onClick()
          }}
        >
          <span className={`w-3 h-3 rounded-full shrink-0 ${colorSet.dot}`} />

          {editing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commitRename}
              className="flex-1 text-sm font-medium bg-transparent border-b border-foreground/30 outline-none min-w-0"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="text-sm font-medium truncate flex-1">{folder.name}</span>
          )}

          <span className="text-xs text-muted-foreground ml-auto shrink-0">
            {folder.app_count}
          </span>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-secondary/80 transition-opacity shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-50"
                align="start"
                sideOffset={4}
              >
                {menuContent}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </FolderContextMenu>
    )
  }
)
